<template>
    <div>
        <!--  Logged in User (or local), or on-site -->
        <div v-if="isLoggedIn || tuningType === 'onsite'">
            <div class="container mt-5">

                <!-- Search Inputs -->
                <div class="bsrow mb-3" v-if="!showResults">
                    <div>
                        <div class="form-field">
                            <!-- Reg -->
                            <div class="reg-container">
                                <dsd-input placeholder="Reg (UK only)" v-model="currentVehicle.reg"
                                   uppercase :maxlength="8" :minlength="1" ref="reg_ecu_input"
                                   @keydown.enter="getServicesByReg"
                                   :disabled="isLoading"
                                >
                                </dsd-input>
                                <button class="button button--primary button--small mb-0" @click="getServicesByReg">Search</button>
                            </div>

                            <p class="w-full text-center my-4">OR</p>

                            <!-- VIN -->
                            <div class="reg-container">
                                <dsd-input placeholder="VIN (Chassis number)" v-model="currentVehicle.vin"
                                           uppercase :maxlength="17" :minlength="17" ref="vin_ecu_input"
                                           @keydown.enter="getServicesByVin"
                                           :disabled="isLoading"
                                >
                                </dsd-input>
                                <button class="button button--primary button--small mb-0" @click="getServicesByVin">Search</button>
                            </div>

                            <p class="w-full text-center my-4">OR</p>

                            <!-- Year/make/model/engine code -->
                            <div class="select-container">
                                <select v-model="selectedVehicle.year" class="select select-bordered w-full max-w-xs p-1">
                                    <option disabled selected :value="null">Select year...</option>
                                    <option v-for="year of selectYearOptions" :value="year">{{year}}</option>
                                </select>
                                <select class="select select-bordered w-full max-w-xs p-1" :disabled="!selectedVehicle.year" v-model="selectedVehicle.make">
                                    <option disabled selected :value="null">Select make...</option>
                                    <option v-for="make of selectMakeOptions" :value="make">{{make}}</option>
                                </select>
                                <select class="select select-bordered w-full max-w-xs p-1" :disabled="!selectedVehicle.year || !selectedVehicle.make" v-model="selectedVehicle.model">
                                    <option disabled selected :value="null">Select model...</option>
                                    <option v-for="model of selectModelOptions" :value="model">{{model}}</option>
                                </select>
                                <select v-model="selectedVehicle.engine_code" class="select select-bordered w-full max-w-xs p-1" :disabled="!selectedVehicle.year || !selectedVehicle.make || !selectedVehicle.model">
                                    <option disabled selected :value="null">Select engine code...</option>
                                    <option v-for="engine_code of selectEngineCodeOptions" :value="engine_code">{{engine_code}}</option>
                                </select>
                                <button class="button button--primary button--small" style="width: 100%;" @click="getServicesByYearMakeModelEngineCode">Search</button>
                            </div>
                        </div>
                    </div>
                </div>

                <!--  Current Vehicle Output  -->
                <div class="content dsd-box mb-4" v-if="showResults">
                    <div class="title-with-button-container">
                        <label class="form-label form-label--alternate form-label--inlineSmall mb-sm">Your Vehicle</label>
                        <a class="button button--secondary button--small mb-2" @click="reset">Start Again</a>
                    </div>

                    <div class="bsrow">
                        <div class="col-md-12">
                            <table class="table mt-1 mb-0">
                                <tr style="border-bottom: 1px solid #dcdcdc;" v-if="currentVehicle.make">
                                    <td>Vehicle Make</td>
                                    <td>{{ currentVehicle.make }}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #dcdcdc;" v-if="currentVehicle.model">
                                    <td>Vehicle Model</td>
                                    <td>{{ currentVehicle.model }}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #dcdcdc;" v-if="currentVehicle.year">
                                    <td>Year</td>
                                    <td>{{ currentVehicle.year }}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #dcdcdc;" v-if="currentVehicle.engine_code">
                                    <td>Engine Code</td>
                                    <td>{{ currentVehicle.engine_code }}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #dcdcdc;" v-if="currentVehicle.bhp">
                                    <td>BHP</td>
                                    <td>{{ currentVehicle.bhp }}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #dcdcdc;" v-if="currentVehicle.gearbox_type">
                                    <td>Transmission Type</td>
                                    <td>{{ currentVehicle.gearbox_type }}</td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- OLD ECU Notice for EDC15V  -->
                <div class="bsrow mb-3" v-if="currentVehicle.ecu && currentVehicle.ecu.includes('EDC15V')">
                    <div class="col-md-12">
                        <div class="form-field d-block">
                            <label class="form-label form-label--alternate form-label--inlineSmall" style="margin-bottom: 0">
                                PLEASE NOTE - Models with a 2 Pin connection will require a 2 Pin to OBD adapter cable
                            </label>
                        </div>
                    </div>
                </div>

                <!--  Results  -->
                <div v-if="remapOptions.length > 0 && showResults">
                    <!-- Main remap options -->
                    <div class="content dsd-box">
                        <label class="form-label form-label--alternate form-label--inlineSmall mb-sm">Select your tune</label>
                        <ul class="options remap no-margin no-padding">
                            <li @click="selectRemapOption('No Remap', 0)" :class="{ 'selected': !selectedRemap }">
                                <div>No Remap</div>
                                <small>ECU software changes without Performance Tuning. Please choose from the
                                    ADDITIONAL OPTIONS
                                    below.</small>
                            </li>
                            <li v-for="(opt, index) in remapOptions" :key="opt.service" v-show="!opt.hidden"
                                :class="{ 'selected': opt.checked }" @click="selectRemapOption(opt, index)">
                                <div class="d-flex">
                                    <div>
                                        {{ opt.service }}
                                        <small v-if="opt.service.toLowerCase().includes('standard car')">ECU Tuning
                                            for vehicles with
                                            STOCK HARDWARE or MINOR MODIFICATIONS including
                                            Exhausts, Intercoolers, Air Filters etc.</small>

                                        <small v-if="opt.service.toLowerCase() === 'remap standard turbo'">ECU
                                            Tuning for vehicles
                                            installed with a BOLT ON TURBO UPGRADE. These are OEM
                                            Turbochargers that fits with the stock downpipe generally found on
                                            higher bhp models.</small>

                                        <small v-if="opt.service.toLowerCase() === 'remap big turbo kit'">ECU Tuning
                                            for vehicles
                                            installed with a DARKSIDE TURBO/KIT (GTB
                                            / GTD Turbocharger or Similar)</small>

                                        <small v-if="opt.service.toLowerCase() === 'nozzles and injectors'">ECU
                                            Tuning for vehicles
                                            installed with Race Injectors / Nozzles.</small>

                                        <small v-if="opt.service.toLowerCase().includes('bespoke tuning')">Bespoke
                                            ECU Tuning for vehicles
                                            that do not fall into the above categories.
                                            Please add the full vehicle specs into the notes above.</small>
                                    </div>
                                    <span class="tuning-price">{{ getPrice(opt.price) }}</span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <!-- Options Lists -->
                    <div class="content dsd-box mt-md">
                        <label class="form-label form-label--alternate form-label--inlineSmall mb-sm">Select any
                            additional
                            options</label>
                        <ul v-if="selectedRemap" class="options remap no-margin no-padding">
                            <li v-for="opt in withRemap" :key="opt.service" @click="selectOption(opt)"
                                :class="{ 'selected': opt.checked }">
                                <div class="d-flex">
                                    <div>
                                        {{ opt.service }}
                                        <small v-if="opt.service.toLowerCase().includes('egr software')">Removes EGR Software from ECU</small>
                                        <small v-if="opt.service.toLowerCase().includes('dpf and egr software')">Removes EGR &amp; DPF Software from ECU</small>
                                        <small v-if="opt.service.toLowerCase().includes('hardcut limiter')">A fuel cut limiter at a set RPM (usually ~4800RPM) making the revs bounce. (Popcorn)</small>
                                        <small v-if="opt.service.toLowerCase().includes('left foot brake')">Disables the OEM power cut when braking and accelerating at the same time. Ideal for track use</small>
                                        <small v-if="opt.service.toLowerCase().includes('vmax removal')">Removes factory speed limitations</small>
                                        <small v-if="opt.service.toLowerCase().includes('hotstart fix')">Fixes starting issue on cars when warm</small>
                                        <small v-if="opt.service.toLowerCase().includes('map switching')">Switch between multiple maps (i.e. Road, Fast Road, Track) Each tune purchase separately</small>
                                        <small v-if="opt.service.toLowerCase().includes('swirl flap')">Removes Swirl Flap software from ECU</small>
                                        <small v-if="opt.service.toLowerCase().includes('cp3')">Calibration for vehicles running a CP3 HPFP (No Tuning)</small>
                                        <small v-if="opt.service.toLowerCase().includes('stage 1')">Low Speed 1st / 2nd Gear Lag Fix, Increase torque limit & Disable Auto Up-Shift In Manual Mode</small>
                                        <small v-if="opt.service.toLowerCase().includes('stage 2')">[ Gearbox Stage 1 ] + Optimised Launch Control</small>
                                        <small v-if="opt.service.toLowerCase().includes('stage 3')">[ Gearbox Stage 1 & 2 ] + Further Torque Limit Increases, Increase Clutch Pressure, Quicker Shifting Times, Shift Lever Delay Removed, Optimised Shift Points in D & S</small>
                                        <small v-if="opt.service.toLowerCase().includes('stage 4')">Please contact us for Pricing for this option! [ Gearbox Stage 1, 2 & 3 ] + Further Torque Limit Increases, Increase Clutch Pressure, Quicker Shifting Times, Shift Lever Delay Removed, Optimised Shift Points in D & S</small>
                                    </div>
                                    <span class="tuning-price">{{ getPrice(opt.price) }}</span>
                                </div>
                            </li>
                        </ul>

                        <!--  options services W/O Remap-->
                        <ul class="options remap no-margin no-padding" v-else>
                            <li v-for="opt in options" :key="opt.service" v-show="!opt.hidden"
                                @click="selectOption(opt)" :class="{ 'selected': opt.checked }">
                                <div class="d-flex">
                                    <div>
                                        {{ opt.service }}
                                        <small v-if="opt.service.toLowerCase().includes('egr software')">Removes EGR
                                            Software from
                                            ECU</small>
                                        <small
                                            v-if="opt.service.toLowerCase().includes('dpf and egr software')">Removes
                                            EGR &amp; DPF
                                            Software from ECU</small>
                                        <small v-if="opt.service.toLowerCase().includes('hardcut limiter')">A fuel
                                            cut limiter at a set
                                            RPM (usually ~4800RPM) making the revs bounce.
                                            (Popcorn)</small>
                                        <!--                  <small v-if="opt.service.toLowerCase().includes('launch control')">A lower RPM limit when stationary or at low speeds (usually &#x3c; 5mph)</small>-->
                                        <small v-if="opt.service.toLowerCase().includes('left foot brake')">Disables
                                            the OEM power cut
                                            when braking and accelerating at the same time. Ideal for track
                                            use</small>
                                        <small v-if="opt.service.toLowerCase().includes('vmax removal')">Removes
                                            factory speed
                                            limitations</small>
                                        <small v-if="opt.service.toLowerCase().includes('hotstart fix')">Fixes
                                            starting issue on cars when
                                            warm</small>
                                        <small v-if="opt.service.toLowerCase().includes('map switching')">Switch
                                            between multiple maps
                                            (i.e. Road, Fast Road, Track) Each tune purchase
                                            separately</small>
                                        <small v-if="opt.service.toLowerCase().includes('swirl flap')">Removes Swirl
                                            Flap software from
                                            ECU</small>
                                        <small v-if="opt.service.toLowerCase().includes('cp3')">Calibration for
                                            vehicles running a CP3
                                            HPFP (No Tuning)</small>
                                        <small v-if="opt.service.toLowerCase().includes('stage 1')">Low Speed 1st /
                                            2nd Gear Lag Fix,
                                            Increase torque limit & Disable Auto Up-Shift In Manual Mode</small>
                                        <small v-if="opt.service.toLowerCase().includes('stage 2')">[ Gearbox Stage
                                            1 ] + Optimised Launch
                                            Control</small>
                                        <small v-if="opt.service.toLowerCase().includes('stage 3')">[ Gearbox Stage
                                            1 & 2 ] + Further Torque
                                            Limit Increases, Increase Clutch Pressure, Quicker Shifting Times, Shift
                                            Lever Delay Removed,
                                            Optimised Shift Points in D & S</small>
                                        <small v-if="opt.service.toLowerCase().includes('stage 4')">Please contact
                                            us for Pricing for this option! [ Gearbox Stage 1, 2 & 3 ] + Further
                                            Torque
                                            Limit Increases, Increase Clutch Pressure, Quicker Shifting Times, Shift
                                            Lever Delay Removed,
                                            Optimised Shift Points in D & S</small>
                                    </div>
                                    <span class="tuning-price">{{ getPrice(opt.price) }}</span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <!-- TOTALS -->
                    <div class="content dsd-box mt-md" v-if="selectedServices.length > 0">
                        <div class="d-flex p-1">
                            <span><b>Subtotal</b></span>
                            <span class="ml-auto"><span v-if="bespokeSelected">From </span>£{{optionsTotal}}</span>
                        </div>
                        <hr style="margin: 10px 0" />
                        <div class="p-1">
                            <ul class="list-style-none">
                                <li v-for="serv in selectedServices" :key="serv.service">
                                    <div class="d-flex">
                                        <span>{{ serv.service }}</span>
                                        <span class="ml-auto">{{ getPrice(serv.price) }}</span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <hr style="margin: 10px 0" />
                        <div class="d-flex p-1">
                            <span><b>Total Price (Inc. VAT)</b></span>
                            <span class="ml-auto"><span v-if="bespokeSelected">From </span><b>£{{
                                (optionsTotal * 1.2).toFixed(2)
                            }}</b></span>
                        </div>
                    </div>

                    <!--  Current Vehicle Mods  -->
                    <div class="content dsd-box mt-md" v-if="tuningType !== 'onsite'">
                        <div class="mb-sm">
                            <label class="form-label form-label--alternate form-label--inlineSmall">List all current vehicle modifications:</label>
                            <textarea class="form-input" v-model="currentVehicle.current_mods" isp_ignore></textarea>
                        </div>
                    </div>

                    <!-- Customer Notes -->
                    <div class="content dsd-box mt-md" v-if="tuningType !== 'onsite'">
                        <div class="mb-sm">
                            <label class="form-label form-label--alternate form-label--inlineSmall">Notes:</label>
                            <textarea class="form-input" v-model="notes" isp_ignore></textarea>
                        </div>
                    </div>
                </div>

                <!-- Terms and Conds -->
                <div class="productView-options productView-options--3col" style="margin: 0" v-if="selectedServices.length > 0 && isLoggedIn && showResults && tuningType !== 'onsite'">
                    <div class="emthemesModez-productView-optionsGrid" style="margin: 0">
                        <div data-product-option-change>
                            <div class="form-field mt-md" data-product-attribute="input-checkbox" style="margin:0;width:100%">
                                <label class="form-label form-label--alternate form-label--inlineSmall">
                                    Terms:
                                    <small>Required</small>
                                </label>
                                <div>
                                    <input type="hidden" name="attribute[2559]" value="2064" />
                                    <input class="form-checkbox" type="checkbox" name="attribute[2559]"
                                        id="attribute-2559_" value="2063" required v-model="terms" />
                                    <label class="form-label" for="attribute-2559_">I have read the terms below.</label>
                                </div>
                                <div style="margin-top:20px">
                                    <input type="hidden" name="attribute[9999]" value="9999" />
                                    <input class="form-checkbox" type="checkbox" name="attribute[9999]"
                                        id="attribute-9999_" value="2063" required v-model="terms2" />
                                    <label class="form-label" for="attribute-9999_">
                                        I accept that Darkside Developments will NOT tune vehicles fitted with hybrid turbos, imitated / copied Darkside Developments products.
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div data-product-option-change v-if="searchedByYMME">
                            <div class="form-field mt-md" data-product-attribute="input-field" style="margin:0;width:100%">
                                <label class="form-label form-label--alternate form-label--inlineSmall">
                                    Confirm your VIN:
                                    <small>Required</small>
                                </label>
                                <p>This can usually be found in the bottom passenger side corner of your windscreen</p>
                                <div>
                                    <input class="form-field" type="text" name="attribute[2650]" id="attribute-2650_" required v-model="currentVehicle.vin" minlength="17" maxlength="17" style="width: 100%;"/>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Login and Add to Cart inputs -->
                    <div class="form-action" style="width: 100%;">
                        <input :disabled="!terms || !terms2 || !currentVehicle.vin || currentVehicle.vin.length < 17" id="form-action-addToCart" class="button button--primary" type="submit" value="Add To Cart" style="color:#fff; width: 100%;" @click="createOrder($event)" v-if="isLoggedIn" />
                        <input id="form-action-addToCart" class="button button--primary" type="submit" value="Login to order" style="color:#fff; width: 100%;" @click="redirectToSignIn($event)" v-else />
                    </div>
                </div>

                <div id="modal_overlay" v-if="showCheckout && tuningType !== 'onsite'" @click="showCheckout = false"></div>

                <dsd-dialog ref="dsddialog"></dsd-dialog>
            </div>
        </div>

        <!--  Not Logged in yet  -->
        <div class="modal-login" v-else>
            <div class="contain d-flex" style="margin-top:40px;">
                <div>
                    <div class="productView-options productView-options--1col" style="text-align: center">
                        <h4>You need to login or create an account before pricing / purchasing a remote tune.</h4>
                        <a class="button button--primary" style="color: #fff" id="trigger-login-modal" @click="redirectToSignIn">
                            SIGN IN
                        </a>

                        <a class="button button--primary" style="color: #fff" id="trigger-login-modal" @click="redirectToCreateAccount">
                            CREATE ACCOUNT
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable no-irregular-whitespace */
import jwt_decode from "jwt-decode";
import axios from "axios";

let api_url = (location.hostname === "localhost") ? "http://127.0.0.1:8000" : "https://api.darkside-developments.com";

export default {
    name: "PriceList",
    data: () => ({
        baseURL: `https://${window.location.hostname}`, // e.g. https://darksidedevelopments.co.uk
        pageURL: `${window.location.pathname}`, // e.g. /products/darkside-powergate-remote-tuning.html
        tuningType: null,

        showResults: false,
        isLoading: false,
        remapOptions: [],
        withRemap: [],
        options: [],
        selectedRemap: false,
        services: [],
        tuningRate: 0,
        currentUser: null,
        currentUserCartId: null,
        notes: "",
        terms: false,
        terms2: false,
        showCheckout: false,
        email: "",
        searchedByYMME: false,

        // define what a vehicle object should look like to perform the compatibility and options selection
        currentVehicle: {
            id: null,
            vin: null,
            reg: null,
            make: null,
            model: null,
            year: null,
            ecu_type: null,
            current_mods: null,
            bhp: null,
            engine_capacity: null,
            engine_code: null,
            gearbox: null,
            gearbox_type: null,
            gearbox_code: null,
            gearbox_model: null,
            drive_type: null
        },

        selectedVehicle: {
            year: null,
            make: null,
            model: null,
            engine_code: null,
        },

        selectYearOptions: [], // automatically generated on mount
        selectMakeOptions: [], // fetched from API
        selectModelOptions: [], // fetched from API, dependent on make
        selectEngineCodeOptions: [], // fetched from API, dependent on make and model

        validMakeOptions: ["Audi", "BMW", "Cupra", "Mini", "Man", "Porsche", "Seat", "Skoda", "Volkswagen"],
    }),
    mounted() {
        // fucked if i know - shame this is the only comment to explain it
        if (this.$refs.select) {
            this.$refs.select.$refs.control.$refs['value-container'].$refs.input.$refs.input.setAttribute('isp_ignore', '');
        }
        this.generateYearOptions();
        this.getTuningType();

        // check if the user is logged in and if so get their
        // information so we can interact with their Cart
        this.getCurrentUser();
        // See if they already have a Cart on BigCommerce or
        // whether we need to create a new one with their Tuning options
        this.getCurrentUsersCart();

        // Local dev fallback user (local environment only)
        if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
            if (!this.currentUser) {
                this.currentUser = {
                    id: 23414,
                    email: "webdev@darksidedevelopments.co.uk"
                };
            }
        }
    },
    methods: {
        processApiResponseData(data) {
            this.currentVehicle = data.vehicle;
            this.remapOptions = data.prices.remap;
            this.withRemap = data.prices.withRemap;
            this.options = data.prices.list;
            this.tuningRate = data.prices.tuningHourlyRate;

            this.remapOptions.forEach(element => {
                if (element.service.includes("Bespoke Tuning")) {
                    element.service = "Bespoke Tuning"
                }
            });

            let cp3 = this.options.filter(elm => elm.service.includes("CP3 Pump"));
            if (cp3 && cp3.length > 0) {
                cp3[0].service = "CP3 Pump Calibration Only"
            }

            this.showResults = true;
        },
        processApiErrorData(err) {
            if (err.response.data) {
                const resp = err.response.data;

                // Missing fields (common 422 case)
                if (resp.data.missing_fields) {
                    console.warn('Missing fields: ', resp.data.missing_fields.join(", "));
                }

                // Structured API error
                if (resp.message) {
                    if (resp.data.message) {
                        const detailMessage = resp.data.message;
                        console.warn(resp.message + ' (' + detailMessage + ')');
                        alert(resp.message + ' (' + detailMessage + ')');
                    } else {
                        console.warn(resp.message);
                        alert(resp.message);
                    }
                }
            } else {
                console.error("Something went wrong getting services! " + err);
                alert("Something went wrong getting services! " + err);
            }
        },
        getServicesByYearMakeModelEngineCode() {
            if (!this.isLoggedIn) {
                alert('NOT LOGGED IN');
                return false;
            }
            if (!this.selectedVehicle.year) {
                alert("Select a valid year");
                return false;
            }
            if (!this.selectedVehicle.make) {
                alert("Select a valid vehicle make");
                return false;
            }
            if (!this.selectedVehicle.model) {
                alert("Select a valid vehicle model");
                return false;
            }
            if (!this.selectedVehicle.model) {
                alert("Select a valid engine code");
                return false;
            }

            this.isLoading = true;

            // call the API for services availability and prices
            axios.post(api_url + "/v3/external/tuning/prices", {
                year: this.selectedVehicle.year,
                make: this.selectedVehicle.make,
                model: this.selectedVehicle.model,
                engine_code: this.selectedVehicle.engine_code,
                type: this.tuningType,
                customer_id: this.currentUser.id
            }).then(resp => {
                this.processApiResponseData(resp.data.data);
                this.searchedByYMME = true;
            }).catch(err => {
                this.processApiErrorData(err);
            }).finally(() => {
                this.isLoading = false;
            });
        },
        getServicesByReg() {
            // check if customer is logged in
            if (!this.isLoggedIn) {
                alert('NOT LOGGED IN');
                return false;
            }
            if (!this.currentVehicle.reg || this.currentVehicle.reg.length < 3) {
                alert("Please enter a valid UK vehicle registration");
                return false;
            }

            this.isLoading = true;
            axios.post(api_url + "/v3/external/tuning/prices", {
                reg: this.currentVehicle.reg,
                type: this.tuningType,
                customer_id: this.currentUser.id
            }).then(resp => {
                this.processApiResponseData(resp.data.data);
            }).catch(err => {
                this.processApiErrorData(err);
            }).finally(() => {
                this.isLoading = false;
            });
        },
        getServicesByVin() {
            // check if customer is logged in
            if (!this.isLoggedIn) {
                alert('NOT LOGGED IN');
                return false;
            }
            if (!this.currentVehicle.vin || this.currentVehicle.vin.length < 17 || this.currentVehicle.vin.length > 17) {
                alert("Please enter a valid vehicle VIN");
                return false;
            }

            this.isLoading = true;
            axios.post(api_url + "/v3/external/tuning/prices", {
                vin: this.currentVehicle.vin,
                type: this.tuningType,
                customer_id: this.currentUser.id
            }).then(resp => {
                this.processApiResponseData(resp.data.data);
            }).catch(err => {
                this.processApiErrorData(err);
            }).finally(() => {
                this.isLoading = false;
            });
        },
        selectOption(opt) {
            if (opt.target) {
                if (opt.target.selectedIndex === 0) {
                    this.remapOptions.forEach((el) => {
                        el.checked = false;
                    });
                    this.withRemap.forEach((el) => {
                        el.checked = false;
                    });
                    this.selectedRemap = false;
                    this.options.forEach(element => {
                        element.hidden = false;
                    });
                    let cp3Pump = this.options.find(elm => elm.service.includes("CP3 Pump"));
                    if (cp3Pump) {
                        cp3Pump.hidden = false;
                        cp3Pump.service = "CP3 Pump Calibration Only";
                    }
                } else {
                    let cp3 = this.options.find(elm => elm.service.includes("CP3 Pump"));

                    this.remapOptions.forEach(el => {
                        el.checked = false;
                    });
                    this.remapOptions[opt.target.selectedIndex - 1].checked = true;
                    this.selectedRemap = true;

                    if (this.remapOptions[opt.target.selectedIndex - 1].service.includes('Pump')) {
                        console.log(cp3)
                        if (cp3) {
                            cp3.hidden = true;
                            cp3.checked = false;
                        }
                    } else {
                        if (cp3) {
                            cp3.hidden = false;
                        }
                    }

                    this.withRemap.forEach(element => {
                        let el = element.service.replace('with Remap', '');
                        let opts = this.options.filter(elm => elm.service === el.trim());
                        opts.forEach(op => {
                            op.hidden = true;
                            op.checked = false;
                        });
                    });

                    if (this.remapOptions[opt.target.selectedIndex - 1].service.startsWith('Remap') || this.remapOptions[opt.target.selectedIndex - 1].service.startsWith('Bespoke')) {
                        if (cp3) {
                            cp3.service = "CP3 Pump";
                        }
                    } else {
                        if (cp3) {
                            cp3.service = "CP3 Pump Calibration Only";
                        }
                    }
                }
            } else {
                // If this is an "Under Development" Option
                if (opt.price === 'UD') {
                    this.$refs.dsddialog.showDialog = true;
                    this.$refs.dsddialog.service = opt;
                    this.$refs.dsddialog.ecu = this.currentVehicle.ecu;
                    return;
                }

                // If this is a gearbox stage option
                if (opt.service.includes('Gearbox Stage')) {
                    // Uncheck all other gearbox stage options
                    const gearboxOptions = [...this.options, ...this.withRemap].filter(
                        item => item.service.includes('Gearbox Stage') && item !== opt
                    );

                    gearboxOptions.forEach(item => {
                        item.checked = false;
                    });
                }

                opt.checked = opt.checked === false;
            }
            this.$forceUpdate();
        },
        selectRemapOption(opt, index) {
            if (opt === "No Remap") {
                // if the customer selects the 'No Remap' option then
                // make sure to un-check and check all currently selected
                this.remapOptions.forEach((el) => {
                    el.checked = false;
                });
                this.withRemap.forEach((el) => {
                    el.checked = false;
                });
                // set selectedRemap to false, so that the watcher picks up the changes
                // and shows/hides the relevant options for that remap choice
                this.selectedRemap = false;

                // The watcher should be doing this
                this.options.forEach(element => {
                    element.hidden = false;
                });

                // look through the options to see if there is one that has  service name CP3 Pump
                let cp3Pump = this.options.find(elm => elm.service.includes("CP3 Pump"));
                // if it's found, set it's hidden property to false so that it is shown on the frontend
                // and rename the service
                if (cp3Pump) {
                    cp3Pump.hidden = false;
                    cp3Pump.service = "CP3 Pump Calibration Only";
                }
            } else {
                // customer has chosen to have a remap

                // look to see if CP3 Pump is in the options that are available
                let cp3 = this.options.find(elm => elm.service.includes("CP3 Pump"));

                // make sure that none of the remapOptions have been pre-selected
                // by defaulting them all to false
                this.remapOptions.forEach(el => {
                    el.checked = false;
                });
                // set the remap option
                this.remapOptions[index].checked = true;
                // update the variable for the watcher to pick up and make the frontend changes to the options
                this.selectedRemap = true;
                // check if we have the CP3 pump in withRemap, passed back from the API
                if (this.remapOptions[index].service.includes('Pump')) {
                    // if we do, then hide the CP3 Pump option that is in the Options array
                    // if it exists
                    if (cp3) {
                        cp3.hidden = true;
                        cp3.checked = false;
                    }
                } else {
                    // else use the Options one instead which will have a larger price
                    if (cp3) {
                        cp3.hidden = false;
                    }
                }
                // foreach option in the withRemap array
                this.withRemap.forEach(element => {
                    // if the name contains 'with Remap', then remove this part of the name
                    const el = element.service.replace('with Remap', '');
                    // this is done to make visual changes to the non-remap service verion to hide them
                    // again, im certain this should be left to the watchers below
                    let opts = this.options.filter(elm => elm.service === el.trim());
                    opts.forEach(op => {
                        op.hidden = true;
                        op.checked = false;
                    });
                });

                // as the above CP3 logic but for bespoke tunes
                if (this.remapOptions[index].service.startsWith('Remap') || this.remapOptions[index].service.startsWith('Bespoke')) {
                    if (cp3) {
                        cp3.service = "CP3 Pump";
                    }
                } else {
                    if (cp3) {
                        cp3.service = "CP3 Pump Calibration Only";
                    }
                }
            }

            // as far as I know the new pricing schema no longer lists service prices as UD
            // - I will need to check that there isn't any logic somewhere else setting null
            // values to 'UD' for example before I completely remove this snippet
            if (this.remapOptions[index].price === 'UD') {
                this.$refs.dsddialog.showDialog = true;
                this.$refs.dsddialog.service = opt;
                this.$refs.dsddialog.ecu = this.currentVehicle.ecu;
                return;
            }
            this.$forceUpdate();
        },
        getTuningType() {
            // onsite:
            // - /ecu-tuning-vag-porsche/
            // - /bmw-ecu-tuning/

            // powergate:
            // - /products/darkside-powergate-remote-tuning.html
            // - /products/darkside-powergate-3-remote-tuning.html

            if (this.pageURL.includes("powergate")) {
                this.tuningType = this.pageURL.includes("powergate-3")
                    ? "powergate3"
                    : "powergate4";
            } else {
                this.tuningType = "onsite"
            }
        },
        getCurrentUser() {
            // get the current customers details like BC ID and email etc.
            // Also checks to see if the customer belongs in any special groups, like 'Trade'
            const self = this;
            const appClientId = "igimdk6jnrt5g5mbwui5gi7f7ke1w5p";
            const xmlhttp = new XMLHttpRequest();
            xmlhttp.onreadystatechange = function () {
                if (xmlhttp.readyState === 4) {
                    if (xmlhttp.status === 200) {
                        let curr_user = jwt_decode(xmlhttp.responseText);
                        self.currentUser = curr_user.customer;
                        self.email = self.currentUser.email;
                        self.GetUserGroup(self.currentUser);
                    } else if (xmlhttp.status === 404) {
                        console.warn("Not logged in!");
                    } else {
                        console.error("Something went wrong");
                    }

                    if (self.$refs.select) {
                        self.$refs.select.$refs.control.$refs['value-container'].$refs.input.$refs.input.setAttribute('isp_ignore', '');
                    }
                }
            };
            xmlhttp.open("GET", `${this.baseURL}/customer/current.jwt?app_client_id=` + appClientId, true);
            xmlhttp.send();
        },
        getCurrentUsersCart() {
            // ping the bigcommerce API to see if the customer already has an existing cart with items in.
            // this is important because they may be using a different currency to GBP
            // Meaning that when we create the cart item, the price is being set - but not converted
            // into the right currency leading to undercharging USA customers etc
            const self = this;
            fetch('/api/storefront/cart', {
                credentials: 'include'
            }).then(function (response) {
                return response.json();
            }).then(function (myJson) {
                if (myJson.length > 0) {
                    self.currentUserCartId = myJson[0].id;
                }
            });
        },
        createOrder(e) {
            // validate we have all the data we need to create a
            // cart item which can be purchased through checkout
            if (this.selectedServices.length === 0) {
                alert("Please select at least one service");
                return;
            }
            if (!this.currentVehicle.vin || this.currentVehicle.vin.length < 17 || this.currentVehicle.vin.length > 17) {
                alert("Please provide the VIN of your vehicle");
                return;
            }
            if (!this.currentVehicle.ecu_type || !this.currentVehicle.model || !this.currentVehicle.make) {
                alert("Please select your vehicle.");
                return;
            }

            e.target.disabled = true;

            // check before sending to API to see if they have an existing cart
            // it does this onMount too, just in case.
            this.getCurrentUsersCart();

            // send the data to our API which will talk to BC API to create a Cart with the selected options and price, and then assign it to the customer
            const url = "https://tuning.darkside-developments.com/api/create/order";
            axios.post(url,
                {
                    user_id: this.currentUser.id,
                    user_email: this.currentUser.email,
                    price: this.optionsTotal,
                    vehicle_id: this.currentVehicle.id,
                    vin: this.currentVehicle.vin,
                    reg: this.currentVehicle.reg,
                    notes: this.notes,
                    ecu: this.currentVehicle.ecu,
                    make: this.currentVehicle.make,
                    model: this.currentVehicle.model,
                    services: JSON.stringify(this.selectedServices),
                    cart_id: this.currentUserCartId,
                    current_mods: this.currentVehicle.current_mods
                }
            ).then(resp => {
                if (resp.data.status === "Success") {
                    window.location.replace(resp.data.msg);
                } else {
                    alert(resp.data.msg)
                }
            }).catch(err => {
                alert("Something went wrong! Please try again or contact us");
                console.error(err);
            }).finally(() => {
                e.target.disabled = false;
            });
        },
        getPrice(val) {
            if (parseInt(val)) {
                return "£" + parseInt(val)
            }
            return val;
        },
        redirectToSignIn() {
            // redirect the customer to the bigcommerce sign-in page as they must be logged in to perform a search/add to basket
            window.location.href = `${this.baseURL}/login.php?redirect_to=${this.pageURL}`;
        },
        redirectToCreateAccount() {
            // redirect the customer to the create account page as they must have an account and be logged in to perform a search/add to basket
            window.location.href = `${this.baseURL}/login.php?redirect_to=${this.pageURL}`;
        },
        generateYearOptions() {
            const currentYear = new Date().getFullYear() + 1; // next year
            const startYear = 1995;

            this.selectYearOptions = [];

            for (let y = currentYear; y >= startYear; y--) {
                this.selectYearOptions.push(y);
            }
        },
        async fetchMakes() {
            const resp = await axios.get(api_url + "/v3/external/tuning/vehicles/makes");
            this.selectMakeOptions = resp.data.data;
        },
        async fetchModels(make) {
            const resp = await axios.get(api_url + "/v3/external/tuning/vehicles/models", {
                params: { make }
            });
            this.selectModelOptions = resp.data.data;
        },
        async fetchEngineCodes(make, model) {
            const resp = await axios.get(api_url + "/v3/external/tuning/vehicles/engines", {
                params: { make, model }
            });
            this.selectEngineCodeOptions = resp.data.data;
        },
        reset() {
            this.currentVehicle = {
                id: null,
                vin: null,
                reg: null,
                make: null,
                model: null,
                year: null,
                ecu_type: null,
                current_mods: null,
                bhp: null,
                engine_capacity: null,
                engine_code: null,
                gearbox: null,
                gearbox_type: null,
                gearbox_code: null,
                gearbox_model: null,
                drive_type: null
            };

            // Force re-confirmation of terms
            this.terms = false;
            this.terms2 = false;

            this.searchedByYMME = false;
            this.showResults = false;
        }
    },
    computed: {
        selectedServices() {
            // merge the customers selected options and remaps into a new array
            // this array will be passed into the API and also added as notes to
            // the order and also the notes in the tuning ticket
            const newArr = this.options.concat(this.remapOptions, this.withRemap);
            const arr = [];
            newArr.forEach(element => {
                if (element.checked) {
                    arr.push(element);
                }
            });
            return arr;
        },
        optionsTotal() {
            // calculate the total cart price based on the customers remap and options selections
            // eg: sum all 'checked' services.prices
            // this total will be passed to the API for when we create the cart item for the customer
            let total = 0;
            this.selectedServices.forEach(el => {
                if (el.checked) {
                    if (parseInt(el.price)) {
                        total += parseInt(el.price);
                    }
                }
            });
            return total;
        },
        bespokeSelected() {
            // select the option with the service name of Bespoke Tuning so that it can be added to the cart
            const tune = this.selectedServices.find(el => el.service === "Bespoke Tuning");
            return !!(tune && tune.checked === true);
        },
        isLoggedIn() {
            return !!this.currentUser;
        },
    },
    watch: {
        // this watcher is responsible for setting the hidden
        // context of an option depending on whether the customer has selected a remap or not
        options: {
            handler() {
                // check if the customer has selected a remap, then hide/show the following options that
                // are not in the RemapOptions for this vehicle
                if (this.selectedRemap) {

                    const hl = this.options.find(el => el.service === "Hardcut Limiter");
                    if (hl) {
                        hl.hidden = false;
                        hl.checked = false
                    }

                    const lc = this.options.find(el => el.service === "Launch Control");
                    if (lc) {
                        lc.hidden = false;
                        lc.checked = false
                    }

                    const egr = this.options.find(el => el.service === "EGR Software");
                    if (egr) {
                        egr.hidden = true;
                        egr.checked = false;
                    }

                    const dpfegr = this.options.find(el => el.service === "DPF and EGR Software");
                    if (dpfegr) {
                        dpfegr.hidden = true;
                        dpfegr.checked = false;
                    }

                    const lfb = this.options.find(el => el.service === "Left Foot Brake");
                    if (lfb) {
                        lfb.hidden = false;
                        lfb.checked = false
                    }

                    const ms = this.options.find(el => el.service === "Map Switching");
                    if (ms) {
                        ms.hidden = false;
                        ms.checked = false
                    }
                }

                // if no remap selected
                else {
                    // Hide these options
                    const hl = this.options.find(el => el.service === "Hardcut Limiter");
                    if (hl) {
                        hl.hidden = true;
                        hl.checked = false
                    }

                    const lc = this.options.find(el => el.service === "Launch Control");
                    if (lc) {
                        lc.hidden = true;
                        lc.checked = false
                    }

                    const lfb = this.options.find(el => el.service === "Left Foot Brake");
                    if (lfb) {
                        lfb.hidden = true;
                        lfb.checked = false
                    }

                    const ms = this.options.find(el => el.service === "Map Switching");
                    if (ms) {
                        ms.hidden = true;
                        ms.checked = false
                    }

                    // Show these options
                    const egr = this.options.find(el => el.service === "EGR Software");
                    if (egr) {
                        egr.hidden = false;
                        egr.checked = false;
                    }

                    const dpfegr = this.options.find(el => el.service === "DPF and EGR Software");
                    if (dpfegr) {
                        dpfegr.hidden = false;
                        // dpfegr.checked = false; Somehow fixes this option not being selectable? Only seems to affect this option?
                    }
                }
            },
            deep: true
        },
        // this watcher is responsible for setting the hidden
        // context of a RemapOption depending on whether the customer has selected a remap or not
        withRemap: {
            handler() {
                let hl, lc, lfb, ms, egr, dpfegr;
                // check if the customer has selected a remap, then hide/show the following RemapOptions that
                // are also in the Options array for this vehicle
                if (this.selectedRemap) {
                    console.log(this.selectedRemap)
                    hl = this.withRemap.find(el => el.service === "Hardcut Limiter");
                    if (hl) {
                        hl.hidden = false;
                        hl.checked = false;
                    }

                    egr = this.withRemap.find(el => el.service === "EGR Software");
                    if (egr) {
                        egr.hidden = false;
                        egr.checked = false;
                    }

                    dpfegr = this.withRemap.find(el => el.service === "DPF and EGR Software");
                    if (dpfegr) {
                        dpfegr.hidden = false;
                        dpfegr.checked = false;
                    }

                    lc = this.withRemap.find(el => el.service === "Launch Control");
                    if (lc) {
                        lc.hidden = false;
                        lc.checked = false;
                    }

                    lfb = this.withRemap.find(el => el.service === "Left Foot Brake");
                    if (lfb) {
                        lfb.hidden = false;
                        lfb.checked = false;
                    }

                    ms = this.withRemap.find(el => el.service === "Map Switching");
                    if (ms) {
                        ms.hidden = false;
                        ms.checked = false;
                    }
                } else {

                    hl = this.withRemap.find(el => el.service === "Hardcut Limiter");
                    if (hl) {
                        hl.hidden = true;
                        hl.checked = false;
                    }

                    egr = this.withRemap.find(el => el.service === "EGR Software");
                    if (egr) {
                        egr.hidden = true;
                        egr.checked = false;
                    }

                    dpfegr = this.withRemap.find(el => el.service === "DPF and EGR Software");
                    if (dpfegr) {
                        dpfegr.hidden = true;
                        dpfegr.checked = false;
                    }

                    lc = this.withRemap.find(el => el.service === "Launch Control");
                    if (lc) {
                        lc.hidden = true;
                        lc.checked = false
                    }

                    lfb = this.withRemap.find(el => el.service === "Left Foot Brake");
                    if (lfb) {
                        lfb.hidden = true;
                        lfb.checked = false
                    }

                    ms = this.withRemap.find(el => el.service === "Map Switching");
                    if (ms) {
                        ms.hidden = true;
                        ms.checked = false
                    }
                }
            },
            deep: true
        },
        'currentVehicle.bhp'(val) {
            // divides by 1 and looks for a
            // remainder to confirm if it's a float
            if (val % 1 !== 0) {
                this.currentVehicle.bhp = Math.round(val)
            }
        },
        'selectedVehicle.year'(year) {
            this.selectedVehicle.make = null;
            this.selectedVehicle.model = null;
            this.selectedVehicle.engine_code = null;
            if (year) this.fetchMakes();
        },
        'selectedVehicle.make'(make) {
            this.selectedVehicle.model = null;
            this.selectedVehicle.engine_code = null;
            if (make) this.fetchModels(make);
        },
        'selectedVehicle.model'(model) {
            this.selectedVehicle.engine_code = null;
            if (model) this.fetchEngineCodes(this.selectedVehicle.make, model);
        },
    }
};
</script>

<style scoped>
.container.mt-5 {
    margin: 0 auto;
    max-width: 800px;
    width: 100%;
}

.form-field {
    text-align: left;
    margin: 0 !important;
}

.d-flex-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
}

.d-flex-container .form-field {
    width: 49% !important;
}

.d-block {
    display: block !important;
    width: 100% !important
}

ul {
    list-style: none
}

ul.options li {
    padding: 10px;
    border: 1px solid #dcdcdc;
    margin-bottom: 10px;
    display: flex;
    cursor: pointer;
    font-weight: bold;
    transition: all 0.2s;
    text-align: left !important;
}

ul.options.remap li {
    display: block;
}

ul.options li:hover {
    box-shadow: 0 2px 4px 3px rgba(0, 0, 0, 0.03),
        0 -2px 4px 3px rgba(0, 0, 0, 0.03);
    background: #eee;
    /*rgba(239, 6, 10, 0.4)*/
}

ul.options li.selected {
    background: #ef060a;
    color: #fff;
}

ul.options li span {
    font-weight: bold;
    margin-left: auto
}

label {
    display: block;
    font-weight: bold;
}

.no-padding {
    padding: 0
}

.no-margin {
    margin: 0;
}

.mt-sm {
    margin-top: 10px !important;
}

.mb-sm {
    margin-bottom: 10px !important;
}

.mt-md {
    margin-top: 20px !important;
}

.mb-md {
    margin-bottom: 20px !important;
}

#modal_overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.85);
    z-index: 99;
}

.checkout-modal {
    position: fixed;
    top: 20px;
    z-index: 999;
    width: auto !important;
}

#container-top span {
    color: #fff;
    text-transform: uppercase;
}

#container,
#container-top,
#container-upsell {
    max-width: 480px;
    height: auto;
    margin: auto;
    font-family: "Oswald", sans-serif;
    text-align: center;
}

#container {
    background: #fff;
    padding: 25px 40px 40px 40px;
    border-radius: 0 0 3px 3px;
}

#container-top {
    background: #444;
    padding: 3px 40px;
    border-radius: 3px 3px 0 0;
}

#container-top p {
    color: #ccc;
    font-size: 14px;
}

#container-top a {
    color: #3ca773;
    text-decoration: none;
}

#secure-tag {
    background: #444;
    text-transform: uppercase;
    font-family: Oswald;
    font-size: 9px;
    color: #ffffff;
    letter-spacing: 1px;
    line-height: 11px;
    padding: 7px 8px;
    margin: 20px auto;
    max-width: 123px;
    border-radius: 3px;
    text-align: center;
}

#container fieldset {
    border: 0;
    margin: 0;
    padding: 0;
}

#container input {
    width: 100%;
    background: #eeeeee;
    box-shadow: inset 0px 1px 0px 0px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    border: 0;
    margin-bottom: 15px;
    font-family: Oswald;
    font-size: 14px;
    color: #888888;
    letter-spacing: 0.2px;
    line-height: 17px;
    padding: 14px 17px;
}

#container input.half-form {
    width: 48.2%;
    margin-right: 10px;
}

#container .last {
    margin-right: 0 !important;
}

#container :focus {
    outline: 0;
}

#container text:focus,
#container input:focus {
    border: 2px solid #3ca773;
    padding: 12px 15px;
    box-shadow: none;
}

/*#container input:valid {
	background-image: url("http://www.clipartbest.com/cliparts/dT7/ojn/dT7ojn8T9.png");
	background-size: 20px;
	background-repeat: no-repeat;
	background-position: 96% 50%;
	color: #444;
	border: 0;
	padding: 14px 17px;
}*/

#container .btn {
    background: #3ca773;
    background-image: none !important;
    border-radius: 4px;
    box-shadow: none;
    font-family: Montserrat-Bold;
    font-size: 11px;
    color: #ffffff !important;
    letter-spacing: 2px;
    line-height: 13px;
    padding: 17px 17px !important;
    border: none;
    margin-bottom: 0;
    text-transform: uppercase;
}

#container a.btn {
    display: block;
    text-decoration: none;
    width: 400px;
}

#container .btn:hover {
    background: #38835f;
}

.input-ecu label {
    display: block;
}

.contain.d-flex {
    justify-content: center;
}

.contain.d-flex>div {
    width: 100%;
}

.contain.d-flex .content {
    width: 100%;
}

.list-style-none {
    list-style: none;
    padding: 0;
    margin: 0;
}

.contain {
    margin: 0 auto;
    max-width: 800px;
    width: 100%;
}

.ml-auto {
    margin-left: auto;
}

.mr-auto {
    margin-right: auto;
}

.d-flex {
    display: flex;
    align-items: center;
}

.icon-btn {
    text-transform: none;
    white-space: nowrap;
    -webkit-box-align: center;
    align-items: center;
    border-radius: 20em;
    text-align: center;
    line-height: 24px;
    font-weight: 500;
    display: flex;
    z-index: 425;
    height: 30px;
    width: 30px;
    align-content: center;
    text-decoration: none;
    background: transparent;
    letter-spacing: normal;
    -webkit-box-pack: center;
    justify-content: center;
}

.service-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.service-input,
.service-list-item {
    transition: 0.25s all ease;
    padding: 0.5em 1.34em;
    border-radius: 6px;
    margin: 10px 0;
    height: 2em;
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.05);
}

.service-input::placeholder {
    font-family: "Montserrat", sans-serif;
}

.service-list-item {
    padding: 0.2em 1em;
    background: white;
    height: 100%;
    max-height: 40px;
}

.service-list-item:hover {
    box-shadow: 0 2px 20px 5px rgba(0, 0, 0, 0.05),
        0 -2px 20px 5px rgba(0, 0, 0, 0.05);
}

.service-list-item.service-list-checked {
    background: rgb(94, 94, 94);
}

.service-list-item.service-list-checked h5 {
    color: #fff;
}

.service-list-item.service-list-checked:hover {
    /*border: 1px solid rgba(231, 49, 94, 0.15);*/
    background: rgba(94, 94, 94, 0.8);
}

.service-list-checkbox {
    position: relative;
}

.service-list-item.service-list-checked .service-list-action .icon-btn {
    background: white;
}

.service-list-item.service-list-checked .service-list-checkbox {
    background: rgba(0, 0, 0, 0.2) !important;
    color: white;
}

.service-list-item.service-list-checked .icon-btn:hover,
.service-list-item.service-list-checked .service-list-checkbox:hover {
    background: rgba(0, 0, 0, 0.4);
    color: white;
}

.service-list-content {
    align-self: center;
}

.service-list-item:hover {
    cursor: pointer;
}

.service-list-item.service-list-checked .service-list-content,
.service-list-item.service-list-checked .service-list-title input {
    color: white;
}

.service-list-title {
    padding-right: 10px;
    font-size: 16px;
}

.service-list-action {
    font-size: 12px;
}

.service-list-title input {
    font-family: "Montserrat", sans-serif;
    background: transparent;
    font-size: 20px;
    padding: 2px 0;
    border: none;
    width: 100%;
}

.service-list-title input.service-list-edit {
    border-bottom: 2px solid #f36;
}

.service-list-item.service-list-checked .service-list-title input.service-list-edit {
    border-bottom: 2px solid #ff85a3;
}

.service-list-disabled {
    color: #ff85a3;
}

h5 {
    margin: 0;
}

@media (min-width: 801px) {
    .productView-options--3col .emthemesModez-productView-optionsGrid .form-field {
        width: calc(50% - 1.5rem);
    }
}

@media (min-width: 551px) {
    .productView-options--3col .emthemesModez-productView-optionsGrid .form-field {
        width: calc(33.33334% - 1.5rem);
    }
}

.productView-options--2col .emthemesModez-productView-optionsGrid .form-field,
.productView-options--3col .emthemesModez-productView-optionsGrid .form-field {
    width: calc(50% - 1.5rem);
    margin: 0 0.75rem 0.76923rem;
    border: 1px solid #dcdcdc;
    padding: 1rem;
    display: inline-block;
    vertical-align: top;
}

.form-field {
    width: 100%;
    margin: 0 0.75rem 0.76923rem;
    border: 1px solid #dcdcdc;
    padding: 1rem;
    display: inline-block;
    vertical-align: top;
}

/* Variables
================================== */
/* Tables
================================== */
.Rtable {
    display: flex;
    flex-wrap: wrap;
    padding: 0;
}

.Rtable-cell {
    box-sizing: border-box;
    flex-grow: 1;
    width: 100%;
    padding: 0.8em 1.2em;
    overflow: hidden;
    list-style: none;
    border: solid 3px white;
    background: rgba(112, 128, 144, 0.2);
}

.Rtable-cell>h1,
.Rtable-cell>h2,
.Rtable-cell>h3,
.Rtable-cell>h4,
.Rtable-cell>h5,
.Rtable-cell>h6 {
    margin: 0;
}

/* Table column sizing
================================== */
.Rtable--1cols>.Rtable-cell {
    width: 100%;
}

.Rtable--2cols>.Rtable-cell {
    width: 50%;
}

.Rtable--3cols>.Rtable-cell {
    width: 33.33%;
}

.Rtable--4cols>.Rtable-cell {
    width: 25%;
}

.Rtable--5cols>.Rtable-cell {
    width: 20%;
}

.Rtable--6cols>.Rtable-cell {
    width: 16.6%;
}

/* Apply styles
================================== */
.Rtable {
    position: relative;
    top: 0px;
    left: 0px;
}

.Rtable-cell {
    margin: -3px 0 0 -0px;
    background-color: #ffffff;
    border-color: #ffffff;
}

/* Cell styles
================================== */
.Rtable-cell--dark {
    background-color: #000;
    border-color: #ffffff;
    color: #ffffff;
}

.Rtable-cell--dark>h1,
.Rtable-cell--dark>h2,
.Rtable-cell--dark>h3,
.Rtable-cell--dark>h4,
.Rtable-cell--dark>h5,
.Rtable-cell--dark>h6 {
    color: #ffffff;
}

.Rtable-cell--medium {
    background-color: #ffffff;
    border-color: #ffffff;
}

.Rtable-cell--light {
    background-color: #ffffff;
    border-color: #ffffff;
}

.Rtable-cell--highlight {
    background-color: #ffffff;
    border-color: #ffffff;
}

.Rtable-cell--alert {
    background-color: #ffffff;
    border-color: #ffffff;
    color: #ffffff;
}

.Rtable-cell--alert>h1,
.Rtable-cell--alert>h2,
.Rtable-cell--alert>h3,
.Rtable-cell--alert>h4,
.Rtable-cell--alert>h5,
.Rtable-cell--alert>h6 {
    color: #ffffff;
}

.Rtable-cell--head {
    background-color: #474747;
    color: #000;
}

.Rtable-cell--head2 {
    background-color: #5e5e5e;
    color: #000;
}

.Rtable-cell--head>h1,
.Rtable-cell--head>h2,
.Rtable-cell--head>h3,
.Rtable-cell--head>h4,
.Rtable-cell--head2>h4,
.Rtable-cell--head>h5,
.Rtable-cell--head>h6 {
    color: #ffffff;
}

.Rtable-cell--foot {
    background-color: #eaeaea;
    color: #000;
}

.Rtable-cell--foot2 {
    background-color: #c1c1c1;
    color: #000;
}

.dsd-box {
    width: 100%;
    border: 1px solid #dcdcdc;
    padding: 1rem;
    display: inline-block;
    vertical-align: top;
}

.Rtable-cell.dsd-box {
    display: block !important;
    background: transparent !important;
    color: #333 !important;
}

.Rtable-cell.dsd-box h4 {
    color: #333;
    font-weight: bold;
}

.info-btn {
    width: 100%;
    color: white;
    font-weight: bold;
    margin-top: 10px;
}

.options li small {
    font-size: 13px;
    display: flex;
    align-items: center;
    font-weight: normal;
}

.options.remap li small {
    font-size: 13px;
    display: flex;
    align-items: center;
    margin-left: 0px;
}

.dsd-banner-grey {
    display: block;
    padding: 10px;
    background: #8A8C8F;
    text-align: center;
    text-transform: uppercase;
    font-weight: bold;
    color: #000;
}

.productView-options--2col .emthemesModez-productView-optionsGrid,
.productView-options--3col .emthemesModez-productView-optionsGrid {
    margin: 0
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity .5s;
}

.fade-enter,
.fade-leave-to

/* .fade-leave-active below version 2.1.8 */
    {
    opacity: 0;
}

ul.options li span.tuning-price {
    width: 50px;
    display: block;
    margin-left: auto;
    text-align: right;
}

.static_page_images .column2 {
    float: left;
    width: 50%;
    padding: 5px;
}

.button.button--primary a {
    color: #fff;
}

.v-select {
    width: 100%;
}

.d-flex {
    display: flex;
}

.dsd-input {
    width: 100%;
}

.bsrow {
    font-weight: 400;
    font-size: 11px;
    justify-content: center;
}

.reg-container {
    display: flex;
    flex-direction: row;
    gap: 10px;
}

.select-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.title-with-button-container {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 10px;
    justify-content: space-between;
}
</style>
